/**
 * What the names and ids in a search stand for (#2290; models for `submodel:`, #2299): `author:"Jane Doe"`
 * needs Jane's profile id before it can search, and `author:<id>` from a
 * link needs Jane's name before its chip can say who.
 *
 * One cache for the page's life (`EntityCache`), filled by lookups and by
 * picks (`learn`: a person picked from the list is known at once, so the
 * name the box then shows never waits). Unknown names read as `pending`
 * until their lookup answers; the lookup waits for typing to pause. The ids
 * wanted at once are named in one lookup (`labelsForIds`, #2301).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QUERY_ID_PATTERN } from '@/types/modelQuery';
import { entityKey, findByName, labelsForIds } from './entityLookups';
import type { EntityKind, EntityLookup, Resolution, Token } from './searchSyntax';

const nameKey = (kind: EntityKind, name: string) => `${kind}\n${name.trim().toLowerCase()}`;
const idKey = (kind: EntityKind, id: string) => `${kind}\n${id.toLowerCase()}`;

/** Names to resolutions and ids to labels. Mutated only by lookups and picks. */
export class EntityCache {
  private readonly names = new Map<string, Resolution>();
  private readonly labels = new Map<string, string>();
  /** Lookups started (in flight or done), so none is asked twice. */
  private readonly asked = new Set<string>();

  byName(kind: EntityKind, name: string): Resolution {
    return this.names.get(nameKey(kind, name)) ?? { status: 'pending' };
  }

  knowsName(kind: EntityKind, name: string): boolean {
    return this.names.has(nameKey(kind, name));
  }

  labelOf(kind: EntityKind, id: string): string | undefined {
    return this.labels.get(idKey(kind, id));
  }

  nameFor(kind: EntityKind, id: string): string | undefined {
    const label = this.labelOf(kind, id);
    if (!label) return undefined;
    const r = this.byName(kind, label);
    return r.status === 'resolved' && r.id === id.toLowerCase() ? label : undefined;
  }

  setName(kind: EntityKind, name: string, r: Resolution): void {
    this.names.set(nameKey(kind, name), r);
  }

  /** An id's label: the label now names that id, unless it already named another. */
  learn(kind: EntityKind, id: string, label: string): void {
    const lid = id.toLowerCase();
    this.labels.set(idKey(kind, lid), label);
    const prev = this.names.get(nameKey(kind, label));
    if (prev?.status === 'resolved' && prev.id !== lid) this.setName(kind, label, { status: 'ambiguous' });
    else if (prev?.status !== 'ambiguous') this.setName(kind, label, { status: 'resolved', id: lid });
  }

  /** Whether to start this lookup now; true at most once per key. */
  ask(key: string): boolean {
    if (this.asked.has(key)) return false;
    this.asked.add(key);
    return true;
  }

  /** A read-only view, a new object per version, so memoised parses re-run. */
  view(_version: number): EntityLookup {
    return {
      byName: (k, n) => this.byName(k, n),
      labelOf: (k, i) => this.labelOf(k, i),
      nameFor: (k, i) => this.nameFor(k, i),
    };
  }
}

/** What the tokens name that the cache does not know yet. */
function wantedOf(tokens: Token[], cache: EntityCache): { key: string; kind: EntityKind; value: string; byId: boolean }[] {
  const out = new Map<string, { key: string; kind: EntityKind; value: string; byId: boolean }>();
  for (const t of tokens) {
    for (const { kind, name } of t.entities ?? []) {
      if (kind === 'author' && name.toLowerCase() === 'me') continue;
      if (QUERY_ID_PATTERN.test(name)) {
        if (cache.labelOf(kind, name) === undefined) out.set(`id\n${idKey(kind, name)}`, { key: `id\n${idKey(kind, name)}`, kind, value: name.toLowerCase(), byId: true });
      } else if (name.trim() && !cache.knowsName(kind, name) && !t.active) {
        out.set(`name\n${nameKey(kind, name)}`, { key: `name\n${nameKey(kind, name)}`, kind, value: name.trim(), byId: false });
      }
    }
  }
  return [...out.values()];
}

export interface EntityResolver {
  lookup: EntityLookup;
  /** Remember a pick: its label names its id from now on. */
  learn: (kind: EntityKind, id: string, label: string) => void;
  /** Look up what these tokens name and the cache does not know, once typing pauses. */
  want: (tokens: Token[]) => void;
}

/**
 * @param onLabel called when an id's label arrives and names it alone, so a
 *   link's `author:<id>` can be shown as `author:"Jane Doe"`
 * @param debounceMs how long typing must pause before a name is looked up
 */
export function useEntityResolver(
  onLabel?: (kind: EntityKind, id: string, name: string) => void,
  debounceMs = 250,
): EntityResolver {
  const [cache] = useState(() => new EntityCache());
  const [version, setVersion] = useState(0);
  const lookup = useMemo(() => cache.view(version), [cache, version]);

  const onLabelRef = useRef(onLabel);
  useEffect(() => { onLabelRef.current = onLabel; });
  const mounted = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const want = useCallback((tokens: Token[]) => {
    const wanted = wantedOf(tokens, cache);
    if (timer.current) clearTimeout(timer.current);
    if (!wanted.length) return;
    timer.current = setTimeout(() => {
      const done = () => { if (mounted.current) setVersion((v) => v + 1); };
      const asked = wanted.filter((w) => cache.ask(w.key));
      const byId = asked.filter((w) => w.byId);
      if (byId.length) {
        labelsForIds(byId.map((w) => ({ kind: w.kind, id: w.value }))).then((labels) => {
          for (const w of byId) {
            const label = labels.get(entityKey(w.kind, w.value));
            if (!label) continue;
            cache.learn(w.kind, w.value, label);
            const name = cache.nameFor(w.kind, w.value);
            if (name && mounted.current) onLabelRef.current?.(w.kind, w.value, name);
          }
        }).catch(() => undefined).finally(done);
      }
      for (const w of asked) {
        if (w.byId) continue;
        findByName(w.kind, w.value).then(({ resolution, hit }) => {
          cache.setName(w.kind, w.value, resolution);
          if (hit) cache.learn(w.kind, hit.id, hit.label);
        }).catch(() => cache.setName(w.kind, w.value, { status: 'failed' })).finally(done);
      }
    }, debounceMs);
  }, [cache, debounceMs]);

  const learn = useCallback((kind: EntityKind, id: string, label: string) => {
    cache.learn(kind, id, label);
    setVersion((v) => v + 1);
  }, [cache]);

  return useMemo(() => ({ lookup, learn, want }), [lookup, learn, want]);
}
